// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import isApiError from "@insite/client-framework/Common/isApiError";
import { Handler, HasOnError, HasOnSuccess, replaceInChain } from "@insite/client-framework/HandlerCreator";
import {
    GetAlsoPurchasedProductCollectionApiV2Parameter,
    getAlsoPurchasedProductsCollectionV2,
    GetProductCollectionApiV2Parameter,
    getProductCollectionV2,
    GetRelatedProductCollectionApiV2Parameter,
    getRelatedProductsCollectionV2,
} from "@insite/client-framework/Services/ProductServiceV2";
import { StorefrontAccess } from "@insite/client-framework/Services/SettingsService";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { chain, RequestDataFromApi } from "@insite/client-framework/Store/Data/Products/Handlers/LoadProducts";
import { ProductCollectionModel, ProductModel } from "@insite/client-framework/Types/ApiModels";

type Parameter = (
    | GetProductCollectionApiV2Parameter
    | GetRelatedProductCollectionApiV2Parameter
    | GetAlsoPurchasedProductCollectionApiV2Parameter
) &
    HasOnSuccess<ProductModel[]> &
    HasOnError<string>;
type Props = {
    apiParameter:
        | GetProductCollectionApiV2Parameter
        | GetRelatedProductCollectionApiV2Parameter
        | GetAlsoPurchasedProductCollectionApiV2Parameter;
    apiResult: ProductCollectionModel;
};

type HandlerType = Handler<Parameter, Props>;

export const XngRequestDataFromApi: HandlerType = async props => {
    const state = props.getState();
    const currentPage = getCurrentPage(state);
    const { storefrontAccess } = getSettingsCollection(state).productSettings;
    if (
        storefrontAccess === StorefrontAccess.SignInRequiredToBrowse &&
        !state.context.session.isAuthenticated &&
        (currentPage.fields.excludeFromSignInRequired || currentPage.type === "SignInPage")
    ) {
        return false;
    }

    try {
        const { onException, isAvendraPricing, brandStandards, ...rest } = props.apiParameter as any;
        if ("relationship" in props.apiParameter) {
            props.apiResult = await getRelatedProductsCollectionV2(props.apiParameter);
        } else if ("productId" in props.apiParameter && props.apiParameter?.type === "alsoPurchased") {
            props.apiResult = await getAlsoPurchasedProductsCollectionV2(props.apiParameter);
        } else {
            props.apiResult = await getProductCollectionV2({
                ...rest,
                properties: JSON.stringify({
                    isAvendraPricing: isAvendraPricing ?? false,
                    brandStandards: brandStandards?.join(",") || "",
                    categoryParents: "",
                    forceRestrictionFilter: true,
                }),
            } as any);
        }
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            props.parameter.onError?.(error.errorJson.message);
            return false;
        }

        if (storefrontAccess === StorefrontAccess.SignInRequiredToBrowse && error.status === 403) {
            // auth timed out with SignInRequiredToBrowsee - throw this up as a 401 so DisplayError reloads the site
            error.status = 401;
            error.errorJson = { message: "error" };
        }
        
        throw error;
    }
};

replaceInChain(chain, RequestDataFromApi, XngRequestDataFromApi);
